import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'scoring'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	criteriaByEvaluation: {} as Record<string, any[]>,
	scoresBySite: {} as Record<string, any[]>,
	summaryByEvaluation: {} as Record<string, any>,
	isLoading: false,
	error: null as string | null,
}

// ============================================================================
// Criteria thunks
// ============================================================================

export const fetchCriteria = createAsyncThunk(
	`${SLICE_NAME}/fetchCriteria`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/criteria`,
				prepConfig(getState),
			)
			return { evalId, criteria: data }
		} catch {
			return rejectWithValue('Failed to load criteria')
		}
	},
)

export const createCriterion = createAsyncThunk(
	`${SLICE_NAME}/createCriterion`,
	async (
		{ evalId, payload }: { evalId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/criteria`,
				payload,
				prepConfig(getState),
			)
			return { evalId, criterion: data }
		} catch {
			return rejectWithValue('Failed to create criterion')
		}
	},
)

export const updateCriterion = createAsyncThunk(
	`${SLICE_NAME}/updateCriterion`,
	async (
		{ evalId, criterionId, payload }: { evalId: string; criterionId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/criteria/${criterionId}`,
				payload,
				prepConfig(getState),
			)
			return { evalId, criterion: data }
		} catch {
			return rejectWithValue('Failed to update criterion')
		}
	},
)

/**
 * Persist a fresh `sortOrder` for every criterion in `orderedIds` (index = new
 * sortOrder). Optimistically reorders the slice's criteria array immediately,
 * then fires per-row PUT /criteria/:id calls in parallel. On failure, refetches
 * to roll back. Used by the drag-reorder flow in CriteriaPanel (E3).
 */
export const reorderCriteria = createAsyncThunk(
	`${SLICE_NAME}/reorderCriteria`,
	async (
		{ evalId, orderedIds }: { evalId: string; orderedIds: string[] },
		{ getState, rejectWithValue, dispatch },
	) => {
		try {
			await Promise.all(
				orderedIds.map((id, idx) =>
					axios.put(
						`/api/evaluations/${evalId}/criteria/${id}`,
						{ sortOrder: idx },
						prepConfig(getState),
					),
				),
			)
			return { evalId, orderedIds }
		} catch {
			// Best-effort rollback: refetch authoritative server order so the
			// optimistic state doesn't drift on partial failure.
			// @ts-ignore — thunks
			dispatch(fetchCriteria(evalId))
			return rejectWithValue('Failed to reorder criteria')
		}
	},
)

export const deleteCriterion = createAsyncThunk(
	`${SLICE_NAME}/deleteCriterion`,
	async (
		{ evalId, criterionId }: { evalId: string; criterionId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			await axios.delete(
				`/api/evaluations/${evalId}/criteria/${criterionId}`,
				prepConfig(getState),
			)
			return { evalId, criterionId }
		} catch {
			return rejectWithValue('Failed to delete criterion')
		}
	},
)

// ============================================================================
// Scores thunks
// ============================================================================

export const fetchSiteScores = createAsyncThunk(
	`${SLICE_NAME}/fetchSiteScores`,
	async (
		{ evalId, siteId }: { evalId: string; siteId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/sites/${siteId}/scores`,
				prepConfig(getState),
			)
			return { siteId, scores: data }
		} catch {
			return rejectWithValue('Failed to load site scores')
		}
	},
)

export const upsertScore = createAsyncThunk(
	`${SLICE_NAME}/upsertScore`,
	async (
		{
			evalId,
			siteId,
			criterionId,
			value,
			justification,
		}: { evalId: string; siteId: string; criterionId: string; value: number; justification?: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/sites/${siteId}/scores`,
				{ criterionId, value, justification },
				prepConfig(getState),
			)
			return { siteId, score: data }
		} catch {
			return rejectWithValue('Failed to save score')
		}
	},
)

// ============================================================================
// Summary thunk
// ============================================================================

export const fetchScoringSummary = createAsyncThunk(
	`${SLICE_NAME}/fetchSummary`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/scores/summary`,
				prepConfig(getState),
			)
			return { evalId, summary: data }
		} catch {
			return rejectWithValue('Failed to load scoring summary')
		}
	},
)

// ============================================================================
// Slice
// ============================================================================

const scoringSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchCriteria.fulfilled, (state, action) => {
				const { evalId, criteria } = action.payload
				state.criteriaByEvaluation[evalId] = criteria
			})
			.addCase(createCriterion.fulfilled, (state, action) => {
				const { evalId, criterion } = action.payload
				if (!state.criteriaByEvaluation[evalId]) state.criteriaByEvaluation[evalId] = []
				state.criteriaByEvaluation[evalId].push(criterion)
				state.criteriaByEvaluation[evalId].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
			})
			.addCase(updateCriterion.fulfilled, (state, action) => {
				const { evalId, criterion } = action.payload
				const list = state.criteriaByEvaluation[evalId] || []
				const idx = list.findIndex((c) => c._id === criterion._id)
				if (idx >= 0) list[idx] = criterion
			})
			.addCase(reorderCriteria.pending, (state, action) => {
				// Optimistic: reorder local state immediately so the UI doesn't
				// flicker waiting for N round-trips. Each row's `sortOrder` is set
				// to its new index; the array is sorted by that field elsewhere.
				const { evalId, orderedIds } = action.meta.arg
				const list = state.criteriaByEvaluation[evalId]
				if (!list) return
				const byId: Record<string, any> = {}
				for (const c of list) byId[c._id] = c
				const reordered = orderedIds
					.map((id, idx) => {
						const entry = byId[id]
						return entry ? { ...entry, sortOrder: idx } : null
					})
					.filter(Boolean) as any[]
				// Append any criteria not in orderedIds (defensive — shouldn't
				// happen in normal flow) at the tail to preserve them.
				for (const c of list) {
					if (!orderedIds.includes(c._id)) reordered.push(c)
				}
				state.criteriaByEvaluation[evalId] = reordered
			})
			.addCase(deleteCriterion.fulfilled, (state, action) => {
				const { evalId, criterionId } = action.payload
				if (state.criteriaByEvaluation[evalId]) {
					state.criteriaByEvaluation[evalId] = state.criteriaByEvaluation[evalId].filter(
						(c) => c._id !== criterionId,
					)
				}
			})
			.addCase(fetchSiteScores.fulfilled, (state, action) => {
				const { siteId, scores } = action.payload
				state.scoresBySite[siteId] = scores
			})
			.addCase(upsertScore.fulfilled, (state, action) => {
				const { siteId, score } = action.payload
				if (!state.scoresBySite[siteId]) state.scoresBySite[siteId] = []
				const existing = state.scoresBySite[siteId].findIndex(
					(s) => s.criterion === score.criterion || s.criterion?._id === score.criterion,
				)
				if (existing >= 0) {
					state.scoresBySite[siteId][existing] = score
				} else {
					state.scoresBySite[siteId].push(score)
				}
			})
			.addCase(fetchScoringSummary.fulfilled, (state, action) => {
				const { evalId, summary } = action.payload
				state.summaryByEvaluation[evalId] = summary
			})
	},
})

// ============================================================================
// Apply a scoring template's criteria to an evaluation.
//
// Mirrors POST /api/evaluations/:evalId/apply-scoring-template. The shared
// `ApplyTemplateModalCN` calls this URL directly via axios — this thunk is a
// Redux-shaped affordance for callers that prefer dispatching (state
// inspection in devtools, error in slice state, etc.).
//
// Returns whatever the server returned; callers should re-fetch criteria +
// summary themselves to refresh derived state.
// ============================================================================
export const applyScoringTemplate = createAsyncThunk(
	`${SLICE_NAME}/applyTemplate`,
	async (
		payload: { evalId: string; templateId: string; overwrite?: boolean },
		{ getState, rejectWithValue },
	) => {
		try {
			const { evalId, templateId, overwrite } = payload
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/apply-scoring-template`,
				{ templateId, overwrite: !!overwrite },
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to apply scoring template',
			)
		}
	},
)

export default scoringSlice
